import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, Car, Wrench, ClipboardList, FileText, Gauge, CalendarIcon, ChevronDown } from 'lucide-react';
import { jobCardSchema, JobCardFormValues, defaultJobCardValues } from './schema';
import { JobCard, JobCardConfig, VehicleCheckedItems, JobCardItemCreate } from '@/app/types/jobCard';
import { VehicleSelector } from '../../components/VehicleSelector';
import { TechnicianSelector } from '../../components/TechnicianSelector';
import { VehicleChecklist } from '../../components/VehicleChecklist';
import { JobCardItemsGrid } from '../../components/JobCardItemsGrid';
import { PartySelector } from '@/app/components/common/PartySelector';
import { useFormHotkeys } from '@/app/hooks/useFormHotkeys';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getVehicle } from '@/app/services/vehicleService';
import { listParties, listPartyGroups } from '@/app/services/partyService';
import { VehicleListItem } from '@/app/types/vehicle';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface JobCardFormProps {
  jobCard?: JobCard;
  config: JobCardConfig;
  onSubmit: (data: JobCardFormValues) => void;
  isLoading?: boolean;
  isEditMode?: boolean;
  initialVehicleId?: number;
  disabled?: boolean;
}

const fuelLevelOptions = [
  { value: 'empty', label: 'Empty' },
  { value: '1/4', label: '1/4' },
  { value: '1/2', label: '1/2' },
  { value: '3/4', label: '3/4' },
  { value: 'full', label: 'Full' },
];

export const JobCardForm: React.FC<JobCardFormProps> = ({
  jobCard,
  config,
  onSubmit,
  isLoading = false,
  isEditMode = false,
  initialVehicleId,
  disabled = false,
}) => {
  const [selectedVehicle, setSelectedVehicle] = React.useState<VehicleListItem | null>(null);
  const [vehicleDetailsOpen, setVehicleDetailsOpen] = useState(isEditMode);
  const [checklistOpen, setChecklistOpen] = useState(isEditMode && !!jobCard?.vehicle_checked_items);
  const [notesOpen, setNotesOpen] = useState(isEditMode && !!jobCard?.notes);

  // Fetch technician group ID
  const { data: techGroupData } = useQuery({
    queryKey: ['partyGroupsForTechnician', 'TECHNICIAN'],
    queryFn: () => listPartyGroups({
      filters: JSON.stringify([{ field: 'name', op: 'ilike', value: 'TECHNICIAN' }]),
      limit: 1,
    }),
    staleTime: 15 * 60 * 1000,
  });

  const techGroupId = techGroupData?.items[0]?.id;

  // Fetch technicians list for the items grid
  const { data: techniciansData } = useQuery({
    queryKey: ['technicians', { groupId: techGroupId }],
    queryFn: () => listParties({
      filters: JSON.stringify([
        { field: 'is_active', op: 'eq', value: true },
        { field: 'party_group_id', op: 'eq', value: techGroupId },
      ]),
      limit: 100,
      sort_by: 'name',
      sort_dir: 'asc',
    }),
    enabled: !!techGroupId,
    staleTime: 5 * 60 * 1000,
  });

  const technicians = useMemo(() =>
    techniciansData?.items?.map(t => ({ id: t.id, name: t.name })) || [],
    [techniciansData]
  );

  const form = useForm<JobCardFormValues>({
    resolver: zodResolver(jobCardSchema),
    defaultValues: jobCard
      ? {
          vehicle_id: jobCard.vehicle_id,
          transaction_date: new Date(jobCard.transaction_date),
          site_id: jobCard.site_id,
          party_id: jobCard.party_id,
          reference_number: jobCard.reference_number,
          notes: jobCard.notes,
          customer_complaint: jobCard.customer_complaint,
          technician_observations: jobCard.technician_observations,
          job_card_type: jobCard.job_card_type,
          priority: jobCard.priority,
          odometer_reading: jobCard.odometer_reading,
          fuel_level: jobCard.fuel_level,
          bay_number: jobCard.bay_number,
          lead_technician_id: jobCard.lead_technician_id,
          estimated_completion_date: jobCard.estimated_completion_date
            ? new Date(jobCard.estimated_completion_date)
            : null,
          vehicle_checked_items: jobCard.vehicle_checked_items,
          items: jobCard.items.map((item) => ({
            id: item.id,
            item_id: item.item_id,
            quantity: parseFloat(item.quantity),
            unit_id: item.unit_id,
            rate: parseFloat(item.rate),
            discount_percent: parseFloat(item.discount_percent || '0'),
            tax_rate_id: item.tax_rate_id,
            notes: item.notes,
            technician_id: item.technician_id,
            estimated_duration_minutes: item.estimated_duration_minutes,
            item: item.item,
            completion_status: item.completion_status,
          })),
        }
      : {
          ...defaultJobCardValues,
          vehicle_id: initialVehicleId || (undefined as unknown as number),
        },
  });

  // Load initial vehicle if provided
  useEffect(() => {
    if (initialVehicleId && !isEditMode) {
      getVehicle(initialVehicleId).then((vehicle) => {
        setSelectedVehicle({
          id: vehicle.id,
          registration_number: vehicle.registration_number,
          make: vehicle.make,
          model: vehicle.model,
          variant: vehicle.variant,
          year_of_manufacture: vehicle.year_of_manufacture,
          color: vehicle.color,
          fuel_type: vehicle.fuel_type,
          transmission: vehicle.transmission,
          owner: vehicle.owner
            ? { id: vehicle.owner.id, name: vehicle.owner.name, phone: vehicle.owner.phone }
            : null,
          last_service_date: vehicle.last_service_date,
          last_service_odometer: vehicle.last_service_odometer,
          created_at: vehicle.created_at,
        });
        form.setValue('vehicle_id', initialVehicleId);
        if (vehicle.owner_id) {
          form.setValue('party_id', vehicle.owner_id);
        }
        setVehicleDetailsOpen(true);
      });
    }
  }, [initialVehicleId, isEditMode, form]);

  // Load vehicle details when editing
  useEffect(() => {
    if (jobCard?.vehicle) {
      setSelectedVehicle({
        id: jobCard.vehicle.id,
        registration_number: jobCard.vehicle.registration_number,
        make: jobCard.vehicle.make,
        model: jobCard.vehicle.model,
        variant: jobCard.vehicle.variant,
        year_of_manufacture: jobCard.vehicle.year_of_manufacture,
        color: jobCard.vehicle.color,
        fuel_type: jobCard.vehicle.fuel_type,
        transmission: jobCard.vehicle.transmission,
        owner: jobCard.vehicle.owner
          ? {
              id: jobCard.vehicle.owner.id,
              name: jobCard.vehicle.owner.name,
              phone: jobCard.vehicle.owner.phone,
            }
          : null,
        last_service_date: jobCard.vehicle.last_service_date,
        last_service_odometer: jobCard.vehicle.last_service_odometer,
        created_at: jobCard.vehicle.created_at,
      });
    }
  }, [jobCard]);

  // Register Ctrl+S hotkey
  useFormHotkeys(() => {
    if (!isLoading) {
      form.handleSubmit(onSubmit)();
    }
  });

  const handleVehicleChange = (vehicleId: number | undefined, vehicle?: VehicleListItem): void => {
    form.setValue('vehicle_id', vehicleId as number);
    if (vehicle) {
      setSelectedVehicle(vehicle);
      setVehicleDetailsOpen(true);
      // Auto-set party from vehicle owner
      if (vehicle.owner?.id && !form.getValues('party_id')) {
        form.setValue('party_id', vehicle.owner.id);
      }
    } else {
      setSelectedVehicle(null);
    }
  };

  const handleChecklistChange = (value: VehicleCheckedItems): void => {
    form.setValue('vehicle_checked_items', value);
  };

  const handleItemsChange = (items: JobCardItemCreate[]): void => {
    form.setValue('items', items as JobCardFormValues['items']);
  };

  const watchedItems = form.watch('items') || [];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 sm:space-y-6 pb-20 sm:pb-0">
        {/* 1. Vehicle & Customer & Date Section */}
        <Card>
          <CardHeader className="px-3 py-2 sm:px-6 sm:py-4">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Car className="h-4 w-4 sm:h-5 sm:w-5" />
              Vehicle & Date
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="col-span-1 sm:col-span-2 lg:col-span-1">
                  <FormLabel>Vehicle *</FormLabel>
                  <FormControl>
                    <VehicleSelector
                      value={field.value}
                      onChange={handleVehicleChange}
                      placeholder="Select vehicle..."
                      disabled={isEditMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="party_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer</FormLabel>
                  <FormControl>
                    <PartySelector
                      value={field.value || undefined}
                      onChange={(value) => field.onChange(value || null)}
                      placeholder="Select customer..."
                      filterPartyType="customer"
                      quickCreateMode
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="transaction_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Date *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? format(field.value, 'PPP') : <span>Select date</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reference_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., REF001"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* 2. Complaint & Observations */}
        <Card>
          <CardHeader className="px-3 py-2 sm:px-6 sm:py-4">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
              Complaint & Observations
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6 grid grid-cols-1 gap-3 sm:gap-4">
            <FormField
              control={form.control}
              name="customer_complaint"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Complaint</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the customer's complaint or service request..."
                      rows={3}
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="technician_observations"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Technician Observations</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Technician's findings and observations..."
                      rows={3}
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* 3. Job Details */}
        <Card>
          <CardHeader className="px-3 py-2 sm:px-6 sm:py-4">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Wrench className="h-4 w-4 sm:h-5 sm:w-5" />
              Job Details
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <FormField
              control={form.control}
              name="job_card_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Type</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value || null)}
                    value={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select job type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {config.job_card_types.map((type) => (
                        <SelectItem key={type.code} value={type.code}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {config.priorities.map((priority) => (
                        <SelectItem key={priority.code} value={priority.code}>
                          {priority.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="bay_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bay Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Bay-1"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lead_technician_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead Technician</FormLabel>
                  <FormControl>
                    <TechnicianSelector
                      value={field.value || undefined}
                      onChange={(value) => field.onChange(value || null)}
                      placeholder="Select technician..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="estimated_completion_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Estimated Completion</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Select date (optional)</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value || undefined}
                        onSelect={(date) => field.onChange(date || null)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* 4. Services & Spare Parts */}
        <Card>
          <CardHeader className="px-3 py-2 sm:px-6 sm:py-4">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5" />
              Services & Spare Parts
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6">
            <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
            <JobCardItemsGrid
              items={watchedItems}
              onChange={handleItemsChange}
              showCompletionStatus={isEditMode}
              technicians={technicians}
            />
            </div>
          </CardContent>
        </Card>

        {/* 5. Vehicle Details (Collapsible) */}
        {selectedVehicle && (
          <Collapsible open={vehicleDetailsOpen} onOpenChange={setVehicleDetailsOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors px-3 py-2 sm:px-6 sm:py-4">
                  <CardTitle className="flex items-center justify-between text-sm sm:text-base">
                    <div className="flex items-center gap-2">
                      <Gauge className="h-4 w-4 sm:h-5 sm:w-5" />
                      Vehicle Details
                    </div>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", vehicleDetailsOpen && "rotate-180")} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Registration</div>
                    <div className="font-medium">{selectedVehicle.registration_number}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Make / Model</div>
                    <div className="font-medium">
                      {selectedVehicle.make || '-'} {selectedVehicle.model || ''}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Owner</div>
                    <div className="font-medium">{selectedVehicle.owner?.name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Last Service</div>
                    <div className="font-medium">
                      {selectedVehicle.last_service_date
                        ? format(new Date(selectedVehicle.last_service_date), 'dd/MM/yyyy')
                        : '-'}
                      {selectedVehicle.last_service_odometer &&
                        ` (${selectedVehicle.last_service_odometer} km)`}
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="odometer_reading"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Current Odometer (km)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="e.g., 45000"
                            {...field}
                            value={field.value || ''}
                            onChange={(e) =>
                              field.onChange(e.target.value ? parseInt(e.target.value, 10) : null)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="fuel_level"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fuel Level</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(value || null)}
                          value={field.value || ''}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select fuel level" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {fuelLevelOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}

        {/* 6. Vehicle Condition Checklist (Collapsible) */}
        <Collapsible open={checklistOpen} onOpenChange={setChecklistOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors px-3 py-2 sm:px-6 sm:py-4">
                <CardTitle className="flex items-center justify-between text-sm sm:text-base">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5" />
                    Vehicle Condition Checklist
                  </div>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", checklistOpen && "rotate-180")} />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6">
                <VehicleChecklist
                  value={form.watch('vehicle_checked_items')}
                  onChange={handleChecklistChange}
                  checklistConfig={config.checklist_items}
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* 7. Additional Notes (Collapsible) */}
        <Collapsible open={notesOpen} onOpenChange={setNotesOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors px-3 py-2 sm:px-6 sm:py-4">
                <CardTitle className="flex items-center justify-between text-sm sm:text-base">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                    Additional Notes
                  </div>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", notesOpen && "rotate-180")} />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="px-3 pb-3 sm:px-6 sm:pb-6">
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder="Any additional notes..."
                          rows={3}
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || null)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Form Actions — sticky on mobile */}
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-background border-t p-3 sm:static sm:border-t-0 sm:p-0 sm:bg-transparent flex justify-end gap-4">
          <Button type="submit" disabled={isLoading || disabled} className="w-full sm:w-auto h-11 sm:h-9 text-sm font-medium">
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? 'Update Job Card' : 'Create Job Card'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
